import {
  Object3D,
  AnimationMixer,
  TextureLoader,
  RepeatWrapping,
  ShaderMaterial,
  Color,
  DoubleSide,
  DoubleSide,
  LinearEncoding,
  Vector3
} from 'three'

import gsap, { Power4 } from 'gsap'
import assets from '../assets'
import camera from '../camera'

import { component } from '../../bidello'
import vertShader from './water.vert.glsl'
import fragShader from './water.frag.glsl'
import skyVertShader from './sky.vert.glsl'
import skyFragShader from './sky.frag.glsl'
import assets from '../assets'
import renderer from '../renderer'

import Home from '../../routes/home'

import floatingVert from './floating.vert.glsl'
import floatingFrag from './floating.frag.glsl'

import displacingVert from './displacing.vert.glsl'
import displacingFrag from './displacing.frag.glsl'

import translatingVert from './translating.vert.glsl'
import translatingFrag from './translating.frag.glsl'

import rotatingVert from './rotating.vert.glsl'
import rotatingFrag from './rotating.frag.glsl'

import baseVert from './base.vert.glsl'
import baseFrag from './base.frag.glsl'

class World extends component(Object3D) {
  async init() {
    this.visible = false
    this.frustumCulled = false
    this.matrixAutoUpdate = false
    this.materialSky = null
    this.materialWater = null
    this.seabedMaterial = null
    this.baseMats = []
    this.floatingMats = []
    this.displacingMats = []
    this.translatingMats = []
    this.rotatingMats = []
  }

  async onLoadEnd() {
    const gltf = assets.resources['rewild_home']
    const scene = gltf.scene || gltf.scenes[0]

    this.clips = gltf.animations || []
    this.camera = gltf.cameras[0] || []

    this.scene = scene

    scene.traverse(async obj => {
      obj.frustumCulled = false

      if (obj.material) {
        if (obj.name.includes('Rotating_')) {
          this.createRotatingMaterial(obj)
        } else if (obj.name.includes('TranslateX_')) {
          this.createTranslatingMaterial(obj)
        } else if (obj.name.includes('Floating_')) {
          this.createFloatingMaterial(obj)
        } else if (obj.name.includes('Displace_')) {
          this.createDisplacingMaterial(obj)
        } else if (obj.name === 'pasted__Water') {
          obj.renderOrder = 3
          this.materialWater = await this.createWaterMesh()
          obj.material.side = DoubleSide
          obj.material = this.materialWater
          obj.material.needsUpdate = true
          obj.material.uniforms.needsUpdate = true
          obj.needsUpdate = true
        } else if (obj.name === 'SkySmall') {
          obj.renderOrder = 1
          this.materialSky = await this.createSkyMesh()
          obj.material = this.materialSky
          this.materialSky.needsUpdate = true
          this.materialSky.uniforms.needsUpdate = true
          obj.needsUpdate = true
          obj.visible = false
        } else if (obj.name === 'pasted__Fondale_mdl') {
          this.createbaseMaterial(obj)
        } else {
          this.createbaseMaterial(obj)
        }
      }
    })

    this.camera.far = 10000
    this.camera.near = 0.01
    this.camera.updateProjectionMatrix()

    this.mixer = new AnimationMixer(scene)

    this.playAllClips()
    this.setTimelines(this.mixer)

    this.add(scene)

    this.resizeCamera = this.resizeCamera.bind(this)
    this.resizeCamera()

    renderer.compile(scene, this.camera)
    window.addEventListener('resize', this.resizeCamera)
  }

  setWorldOpacity(value) {
    this.rotatingMats.forEach(mat => {
      mat.uniforms.uFade.value = value
    })
    this.floatingMats.forEach(mat => {
      mat.uniforms.uFade.value = value
    })
    this.displacingMats.forEach(mat => {
      mat.uniforms.uFade.value = value
    })

    this.translatingMats.forEach(mat => {
      mat.uniforms.uFade.value = value
    })

    this.baseMats.forEach(mat => {
      mat.uniforms.uFade.value = value
    })

    this.materialSky.uniforms.uFade.value = value
    this.materialWater.uniforms.uFade.value = value
  }

  onEnterHome() {
    if (!this.visible) {
      const dummy = { value: 0 }
      const tl = gsap.timeline()
      this.visible = true
      // smoke.visible = true

      tl
        // .to(smoke.material.uniforms.uFade, {
        //   duration: 0.8,
        //   value: 0,
        //   ease: Power4.easeOut
        // })
        // .add(() => {
        //   this.visible = true
        //   smoke.visible = false
        // })
        .to(dummy, {
          duration: 0.8,
          value: 1,
          ease: Power4.easeOut,
          onUpdate: () => {
            this.setWorldOpacity(dummy.value)
          }
        })
    }
  }

  onLeaveHome() {
    if (this.visible) {
      const dummy = { value: 1 }
      const tl = gsap.timeline({
        onComplete: () => {
          Home.destroyTriggers()
        }
      })

      tl
        .to(dummy, {
          duration: 0.8,
          value: 0,
          ease: Power4.easeOut,
          onUpdate: () => {
            this.setWorldOpacity(dummy.value)
          }
        })
        .add(() => {
          this.visible = false
          // smoke.visible = true
        })
        // .fromTo(smoke.material.uniforms.uFade, {
        //   value: 0
        // },{
        //   duration: 0.8,
        //   value: 1,
        //   ease: Power4.easeOut
        // })
    }
  }

  onTlProgress({ type, progress }) {
    if (this[type]) {
      this[type].progress(progress)
    }
  }

  resizeCamera() {
    this.camera.aspect = window.innerWidth / window.innerHeight
    this.camera.updateProjectionMatrix()
    renderer.setSize(window.innerWidth, window.innerHeight)
  }

  destroy() {
    window.removeEventListener('resize', this.resizeCamera)
  }

  createbaseMaterial(obj) {
    obj.renderOrder = 4
    const { map } = obj.material
    map.encoding = LinearEncoding

    const uniforms = {
      uMap: { value: map },
      uFade: { value: 1 },
    }

    obj.material = new ShaderMaterial({
      transparent: true,
      uniforms,
      vertexShader: baseVert,
      fragmentShader: baseFrag,
      depthWrite: false,
      side: DoubleSide,
    })

    this.baseMats.push(obj.material)

    if (obj.name === 'pasted__Fondale_mdl') {
      obj.renderOrder = 2
    }
  }

  createRotatingMaterial(obj) {
    obj.renderOrder = 3
    const { map } = obj.material
    map.encoding = LinearEncoding

    const uniforms = {
      uTime: { value: 0 },
      uMap: { value: map },
      uFade: { value: 1 },
      uRandom: { value: Math.random() },
      uAxis: { value: new Vector3(0, 1, 0) }
    }

    obj.material = new ShaderMaterial({
      transparent: true,
      uniforms,
      vertexShader: rotatingVert,
      fragmentShader: rotatingFrag,
      depthWrite: false,
      side: DoubleSide,
    })

    this.rotatingMats.push(obj.material)

    if (obj.name.includes('Rotating_Lotus')) {
      obj.renderOrder = 5
    }
  }

  createTranslatingMaterial(obj) {
    obj.renderOrder = 3
    const { map } = obj.material
    map.encoding = LinearEncoding

    const uniforms = {
      uProgress: { value: 0 },
      uMap: { value: map },
      uFade: { value: 1 }
    }

    obj.material = new ShaderMaterial({
      transparent: true,
      uniforms,
      vertexShader: translatingVert,
      fragmentShader: translatingFrag,
      depthWrite: false,
      side: DoubleSide,
    })

    this.translatingMats.push(obj.material)
  }

  createFloatingMaterial(obj) {
    obj.renderOrder = 3
    const { map } = obj.material
    map.encoding = LinearEncoding

    const uniforms = {
      uTime: { value: 0 },
      uMap: { value: map },
      uFade: { value: 1 },
      uRandom: { value: Math.random() }
    }

    obj.material = new ShaderMaterial({
      transparent: true,
      uniforms,
      vertexShader: floatingVert,
      fragmentShader: floatingFrag,
      depthWrite: false,
      side: DoubleSide,
    })

    this.floatingMats.push(obj.material)
  }

  createDisplacingMaterial(obj) {
    obj.renderOrder = 3
    const { map } = obj.material
    map.encoding = LinearEncoding

    const uniforms = {
      uTime: { value: 0 },
      uMap: { value: map },
      uFade: { value: 1 }
    }

    obj.material = new ShaderMaterial({
      transparent: true,
      uniforms,
      vertexShader: displacingVert,
      fragmentShader: displacingFrag,
      depthWrite: false,
      side: DoubleSide,
    })

    this.displacingMats.push(obj.material)
  }

  async createSkyMesh() {
    const textureLoader = new TextureLoader()
    const noiseTexture = await textureLoader.load('/app/themes/rewild-yourself/dist/noise.webp')
    const starsTexture = await textureLoader.load('/app/themes/rewild-yourself/dist/stars.webp')
    const atmoTexture = await textureLoader.load('/app/themes/rewild-yourself/dist/atmosphere.webp')

    noiseTexture.wrapS = noiseTexture.wrapT = atmoTexture.wrapS = atmoTexture.wrapT = starsTexture.wrapS = starsTexture.wrapT = RepeatWrapping
    noiseTexture.needsUpdate = atmoTexture.needsUpdate = starsTexture.needsUpdate = true

    const uniforms = {
      uNoise: { value: noiseTexture },
      uAtmosphere: { value: atmoTexture },
      uStar: { value: starsTexture },
      uTime: { value: 0 },
      uColor: { value: new Color(0x023744) },
      uDpr: { value: 1.5 },
      uFade: { value: 0 },
      uResolution: { value: [1920, 1080] },
    }

    return new ShaderMaterial({
      transparent: true,
      uniforms,
      vertexShader: skyVertShader,
      fragmentShader: skyFragShader
    })
  }

  async createWaterMesh() {
    const waterLinesTexture = await new TextureLoader().load('/app/themes/rewild-yourself/dist/WaterTexture.webp')
    waterLinesTexture.wrapS = RepeatWrapping
    waterLinesTexture.wrapT = RepeatWrapping
    waterLinesTexture.needsUpdate = true

    const uniforms = {
      uTime: { value: 0 },
      uFade: { value: 1 },
      uSurfaceTexture: {type: "t", value: waterLinesTexture },
    }

    return new ShaderMaterial({
      transparent: true,
      uniforms,
      vertexShader: vertShader,
      fragmentShader: fragShader,
      side: DoubleSide,
      depthWrite: false
    })
  }


  loadGltf() {
    return new Promise(resolve => {
      this.loader.load(assets.resources.rewild_home.url, gltf => {
        resolve(gltf)
      })
    })
  }

  onRaf({ now }) {
    if (this.visible) {
      if (this.rotatingMats.length) {
        this.rotatingMats.forEach(material => {
          material.uniforms.uTime.value += 0.03
        })
      }

      if (this.floatingMats.length) {
        this.floatingMats.forEach(material => {
          material.uniforms.uTime.value += 0.03
        })
      }

      if (this.displacingMats.length) {
        this.displacingMats.forEach(material => {
          material.uniforms.uTime.value += 0.03
        })
      }

      if (this.materialWater) {
        this.materialWater.uniforms.uTime.value += 0.0015
      }

      if (this.materialSky) {
        this.materialSky.uniforms.uTime.value += 0.0003
      }

      if (this.backgroundMesh) {
        this.backgroundMesh.position.copy(camera.position)
        this.backgroundMesh.lookAt(camera)
        this.backgroundMesh.position.z = -20
      }
    }
  }

  reset() {
    this.tl0.progress(0)
    this.setWorldOpacity(1)

    this.materialSky.uniforms.uFade.value = 0
  }

  playAllClips() {
    this.clips.forEach(clip => {
      if (clip.name.includes('Render_Cam')) {
        this.cameraClip = clip.clone()
      }
      this.mixer.clipAction(clip).play()
    })
  }

  skipStartup() {
    const mixer = this.mixer
    const mixerProxy = {
      get time() {
        return mixer?.time
      },
      set time(value) {
        mixer?.setTime(value)
      },
    }

    gsap.fromTo(mixerProxy, {
      time: 10,
    }, {
      duration: 2,
      time: 11.5
    })
  }

  setTimelines(mixer) {
    const mixerProxy = {
      get time() {
        return mixer?.time
      },
      set time(value) {
        mixer?.setTime(value)
      },
    }

    this.tl0 = gsap.timeline({ paused: true })
    this.tl0.to(mixerProxy, { time: 3.82 })

    this.tl1 = gsap.timeline({ paused: true })
    this.tl1.to(mixerProxy, { time: 11.5 })

    this.tl2 = gsap.timeline({ paused: true })
    this.tl2.to(mixerProxy, { time: 13 })

    this.tl3 = gsap.timeline({ paused: true })
    this.tl3.to(mixerProxy, { time: 14.6 })

    this.tl4 = gsap.timeline({ paused: true })
    this.tl4.to(mixerProxy, { time: 33.8 })

    this.tl5 = gsap.timeline({ paused: true })
    this.tl5.to(mixerProxy, { time: 46 })

    this.tl6 = gsap.timeline({ paused: true })
    this.tl6.to(mixerProxy, { time: 49.5 })

    this.tl7 = gsap.timeline({ paused: true })
    this.tl7.to(mixerProxy, { time: 53 })

    this.tl8 = gsap.timeline({ paused: true })
    this.tl8.to(mixerProxy, { time: 56 })

    this.tl9 = gsap.timeline({ paused: true })
    this.tl9.to(mixerProxy, { time: 59 })

    this.tl10 = gsap.timeline({ paused: true })
    this.tl10.to(mixerProxy, { time: 62 })
  }
}

export default new World()

